import type { TFunction } from "i18next";
import { formatPersonFullName } from "../../../utils/formatters";
import type { NurseAction, NurseResult } from "../hooks";
import { floatPoolMergeRows } from "./floatPoolMerge";
import { floatPoolLines } from "./floatPoolSummaryLines";

const SUMMARY = "onboarding_load.summary";

export function deriveAction(result: NurseResult): NurseAction {
	if (result.action) return result.action;
	const isSuccess = result.success === true || result.success === "True";
	return isSuccess ? "created" : "failed";
}

export function formatChangeValue(val: unknown): string {
	if (val === null || val === undefined || val === "") return "—";
	if (typeof val === "object") return JSON.stringify(val);
	return String(val);
}

export function groupResultsByAction(results: NurseResult[]): Record<NurseAction, NurseResult[]> {
	const groups: Record<NurseAction, NurseResult[]> = { created: [], linked: [], held: [], updated: [], unchanged: [], failed: [] };
	for (const r of results) groups[deriveAction(r)].push(r);
	return groups;
}

export function nurseDisplayName(result: NurseResult, t: TFunction, lastNameFirst: boolean): string {
	// DEFAULT: a row may lack either name part; the missing part is left out, and a row with neither reads "unknown".
	const name = formatPersonFullName({ firstName: result.first_name || "", lastName: result.last_name || "" }, lastNameFirst);
	return name || t(`${SUMMARY}.unknown_name`);
}

/** A titled list of lines, followed by a blank line — or nothing when the list is empty. */
function section(title: string, items: string[]): string[] {
	return items.length > 0 ? [title, ...items, ""] : [];
}

function createdLines(created: NurseResult[], t: TFunction, lastNameFirst: boolean): string[] {
	const items = created.map((r) =>
		r.nurse_id
			? t(`${SUMMARY}.created_item`, { name: nurseDisplayName(r, t, lastNameFirst), id: r.nurse_id })
			: t(`${SUMMARY}.name_item`, { name: nurseDisplayName(r, t, lastNameFirst) }),
	);
	return section(t(`${SUMMARY}.created_section`, { count: created.length }), items);
}

function updatedLines(updated: NurseResult[], t: TFunction, lastNameFirst: boolean): string[] {
	const items = updated.flatMap((r) => {
		const lines = [t(`${SUMMARY}.updated_item`, { name: nurseDisplayName(r, t, lastNameFirst) })];
		// A row can be "updated" with only skipped contact to report, so changes may be absent.
		if (r.changes) {
			for (const [field, change] of Object.entries(r.changes)) {
				lines.push(t(`${SUMMARY}.change_item`, { field, old: formatChangeValue(change.old), new: formatChangeValue(change.new) }));
			}
		}
		const skipped: string[] = [];
		if (r.skipped_fields?.email) skipped.push(t(`${SUMMARY}.skipped_email`, { value: r.skipped_fields.email }));
		if (r.skipped_fields?.phone_number) skipped.push(t(`${SUMMARY}.skipped_phone`, { value: r.skipped_fields.phone_number }));
		if (skipped.length > 0) lines.push(t(`${SUMMARY}.skipped_fields`, { fields: skipped.join(", ") }));
		return lines;
	});
	return section(t(`${SUMMARY}.updated_section`, { count: updated.length }), items);
}

/** Held rows: not created, each with the records elsewhere in the network that carry its name. */
function heldLines(held: NurseResult[], t: TFunction, lastNameFirst: boolean): string[] {
	const items = held.map((r) => {
		// A held row always lists its namesakes; one whose records could not be described shows its reason instead.
		const records = r.held_candidates?.map((record) => `#${record.nurseId} (${record.departments[0].name})`).join(", ");
		return records
			? t(`${SUMMARY}.held_item`, { name: nurseDisplayName(r, t, lastNameFirst), records })
			: t(`${SUMMARY}.failure_item`, { name: nurseDisplayName(r, t, lastNameFirst), reason: r.reason });
	});
	return section(t(`${SUMMARY}.held_section`, { count: held.length }), items);
}

type FailureBuckets = {
	invalidRoles: Map<string, string[]>;
	sameEmailDiffPhone: string[];
	samePhoneDiffEmail: string[];
	duplicateNurse: string[];
	otherFailures: Array<{ name: string; reason: string }>;
};

/** The role named in "invalid role: <error>, valid options are: ...". */
function invalidRoleOf(reason: string): string {
	const roleMatch = reason.match(/invalid role:\s*[^,]*'([^']+)'/);
	return roleMatch
		? roleMatch[1]
		: reason
				.replace(/invalid role:\s*/, "")
				.split(",")[0]
				.trim();
}

function bucketFailures(failed: NurseResult[], t: TFunction, lastNameFirst: boolean): FailureBuckets {
	const buckets: FailureBuckets = { invalidRoles: new Map(), sameEmailDiffPhone: [], samePhoneDiffEmail: [], duplicateNurse: [], otherFailures: [] };
	for (const r of failed) {
		const name = nurseDisplayName(r, t, lastNameFirst);
		const reason = String(r.reason);
		if (reason.startsWith("invalid role:")) {
			const role = invalidRoleOf(reason);
			const sameRole = buckets.invalidRoles.get(role);
			if (sameRole) sameRole.push(name);
			else buckets.invalidRoles.set(role, [name]);
		} else if (reason.includes("duplicate nurse") || reason.includes("nurse exists")) {
			// Which contact the row carried says which kind of duplicate it hit; with both or neither it is a general one.
			const hasEmail = Boolean(r.email);
			const hasPhone = Boolean(r.phone_number);
			if (hasEmail && !hasPhone) buckets.sameEmailDiffPhone.push(name);
			else if (hasPhone && !hasEmail) buckets.samePhoneDiffEmail.push(name);
			else buckets.duplicateNurse.push(name);
		} else {
			buckets.otherFailures.push({ name, reason });
		}
	}
	return buckets;
}

function failureLines(failed: NurseResult[], t: TFunction, lastNameFirst: boolean): string[] {
	const buckets = bucketFailures(failed, t, lastNameFirst);
	const names = (list: string[]) => list.map((name) => t(`${SUMMARY}.name_item`, { name }));
	const invalidRoleTotal = [...buckets.invalidRoles.values()].reduce((sum, list) => sum + list.length, 0);
	return [
		...section(
			t(`${SUMMARY}.invalid_role_section`, { count: invalidRoleTotal }),
			[...buckets.invalidRoles].map(([role, list]) => t(`${SUMMARY}.invalid_role_item`, { role, count: list.length })),
		),
		...section(t(`${SUMMARY}.same_email_diff_phone_section`, { count: buckets.sameEmailDiffPhone.length }), names(buckets.sameEmailDiffPhone)),
		...section(t(`${SUMMARY}.same_phone_diff_email_section`, { count: buckets.samePhoneDiffEmail.length }), names(buckets.samePhoneDiffEmail)),
		...section(t(`${SUMMARY}.duplicate_section`, { count: buckets.duplicateNurse.length }), names(buckets.duplicateNurse)),
		...section(
			t(`${SUMMARY}.other_failures_section`, { count: buckets.otherFailures.length }),
			buckets.otherFailures.map(({ name, reason }) => t(`${SUMMARY}.failure_item`, { name, reason })),
		),
	];
}

/** The copyable plain-text summary of a roster load, in the onboarder's language. */
export function buildNursesSummaryText(results: NurseResult[], total: number, t: TFunction, lastNameFirst: boolean): string {
	const groups = groupResultsByAction(results);
	const mergeRows = floatPoolMergeRows(results, lastNameFirst);
	const mergeSources = new Set(mergeRows.map((mergeRow) => mergeRow.source));
	const header = t(`${SUMMARY}.header`, {
		created: groups.created.length,
		linked: groups.linked.length,
		held: groups.held.length,
		updated: groups.updated.length,
		unchanged: groups.unchanged.length,
		failed: groups.failed.length,
		total,
	});
	const lines = [header];
	if (mergeSources.size > 0) lines.push(t(`${SUMMARY}.already_on_record`, { count: mergeSources.size }));
	lines.push(
		"",
		...heldLines(groups.held, t, lastNameFirst),
		...floatPoolLines(groups.linked, mergeRows, t, lastNameFirst),
		...createdLines(groups.created, t, lastNameFirst),
		...updatedLines(groups.updated, t, lastNameFirst),
	);
	// Rows listed in the merge section are not listed again as failures.
	lines.push(
		...failureLines(
			groups.failed.filter((r) => !mergeSources.has(r)),
			t,
			lastNameFirst,
		),
	);
	return lines.join("\n").trim();
}
